import { TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import { createForm } from '@felte/solid';
import BottomSheet from '@mobileComponents/BottomSheet';
import { creditIncreaseRequest } from '@mobileStore/customer/services';
import { handleToast } from '@utils/utils';
import { createSignal } from 'solid-js';

type CustomerRequestCreditProps = {
  isOpen: boolean;
  customerId: number;
  onClose: () => void;
  onSubmitSuccess: () => void;
};

type FormState = {
  amount: number;
  reason: string;
};

const preventingNonNumeric = (e: KeyboardEvent) => {
  if (e.key === 'e' || e.key === '-' || e.key === '+') {
    e.preventDefault();
  }
};

const sanitizeAmount = (value: unknown): number => {
  const val = Number(value);
  return Number.isFinite(val) && val > 0 ? val : 0;
};

export const CustomerRequestCredit = (
  props: Readonly<CustomerRequestCreditProps>,
) => {
  const [isLoading, setIsLoading] = createSignal(false);

  const { form, reset, data, setFields } = createForm<FormState>({
    initialValues: { amount: 0, reason: '' },
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const res = await creditIncreaseRequest({
          ...values,
          customerId: String(props.customerId),
        });
        if (res?.isSuccess) {
          reset();
          handleToast(ToastType.Success, 'Credit request was submitted');
          props.onSubmitSuccess();
          props.onClose();
        } else {
          const message =
            res?.errors[0]?.message ?? 'Failed to submit credit request';
          handleToast(ToastType.Error, message);
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <BottomSheet
      title="Request More Credit"
      isOpen={props.isOpen}
      onClose={() => {
        reset();
        props.onClose();
      }}
    >
      <form ref={form} class="flex flex-col gap-2">
        <TextField
          fontSize="16px"
          label="Amount"
          dataTestId="credit-request-amount"
          type="number"
          placeholder="Enter Amount here"
          value={data().amount}
          onChange={(e: unknown) => {
            setFields('amount', sanitizeAmount(e));
          }}
          startIcon="$"
          maxLength={10}
          onKeyPress={preventingNonNumeric}
        />
        <TextField
          fontSize="16px"
          label="Reason/Comment"
          dataTestId="credit-request-reason"
          placeholder="Enter Comment here"
          value={data().reason}
          onChange={(e: string) => {
            setFields('reason', e);
          }}
          maxLength={4000}
        />
        <Button
          label="Submit Request"
          dataTestId="credit-request-submit"
          disabled={!(data().amount > 0) || isLoading()}
          type="submit"
          variant="contained"
          color="primary"
          isLoading={isLoading()}
          class="w-full"
        />
      </form>
    </BottomSheet>
  );
};
